<template>
  <main class="booking-store">

    <!-- 返回首頁列 -->
    <div class="bs-topbar">
      <router-link to="/" class="bs-back-btn">← 返回首頁</router-link>
    </div>

    <!-- 載入中 -->
    <div v-if="bookingStore.storeLoading" class="bs-state">
      <span class="bs-spinner" />
      <p>載入店家資料…</p>
    </div>

    <!-- 店家不存在 -->
    <div v-else-if="bookingStore.storeError" class="bs-state bs-state--error">
      <p class="bs-state__title">找不到此店家</p>
      <p class="bs-state__msg">{{ bookingStore.storeError }}</p>
      <router-link :to="{ name: 'BookingHome' }" class="bs-btn bs-btn--secondary">
        ← 返回店家列表
      </router-link>
    </div>

    <!-- 正常內容 -->
    <template v-else-if="store">
      <!-- 封面 -->
      <div class="bs-cover-wrap">
      <div class="bs-cover">
        <img
          v-if="store.cover_image"
          :src="resolveImg(store.cover_image)"
          :alt="store.name"
          class="bs-cover__img"
        />
        <div v-else class="bs-cover__placeholder" />
        <div class="bs-cover__overlay">
          <h1 class="bs-cover__name">{{ store.name }}</h1>
          <p v-if="store.address" class="bs-cover__address">{{ store.address }}</p>
        </div>
      </div>
      </div>

      <div class="bs-body">
        <!-- 返回 -->
        <router-link :to="{ name: 'BookingHome' }" class="bs-back">
          ← 返回店家列表
        </router-link>

        <!-- 從「變更時間」跳回來的提示 -->
        <div v-if="isReschedule" class="bs-reschedule-banner">
          ✅ 原預約已取消，請重新選擇服務與時段
        </div>

        <div class="bs-layout">
          <!-- 左欄：服務項目 -->
          <section class="bs-services">
            <h2 class="bs-section-title">服務項目</h2>

            <!-- 服務分類標籤 -->
            <div v-if="bookingStore.currentCategories.length" class="bs-category-tags">
              <span
                v-for="cat in bookingStore.currentCategories"
                :key="cat.id"
                class="bs-category-tag"
              >{{ cat.name }}</span>
            </div>

            <div
              v-if="bookingStore.currentServices.length === 0 && bookingStore.currentCategories.length === 0"
              class="bs-empty"
            >
              目前無提供服務
            </div>

            <div
              v-for="svc in bookingStore.currentServices"
              :key="svc.id"
              class="bs-service-card"
            >
              <!-- 老師名稱 + 簡介 -->
              <p class="bs-service-card__name">{{ svc.name }}</p>
              <p v-if="svc.description" class="bs-service-card__desc">
                {{ svc.description }}
              </p>

              <!-- 有 service_items：每項目各自預約按鈕 -->
              <template v-if="svc.service_items && svc.service_items.length">
                <div
                  v-for="item in svc.service_items"
                  :key="item.id"
                  class="bs-service-item-row"
                >
                  <div class="bs-service-item-info">
                    <span class="bs-service-item-name">{{ item.name }}</span>
                    <span class="bs-service-item-meta">
                      約 {{ item.duration_minutes }} 分・NT$ {{ Number(item.price).toLocaleString() }}{{ item.price_note ? ' ' + item.price_note : '' }}
                    </span>
                  </div>
                  <button
                    class="bs-btn bs-btn--primary bs-btn--sm"
                    @click="goReserve(svc.id, item)"
                  >
                    立即預約
                  </button>
                </div>
              </template>

              <!-- 無 service_items fallback -->
              <template v-else>
                <div class="bs-service-item-row">
                  <div class="bs-service-item-info">
                    <span class="bs-service-item-meta">
                      約 {{ svc.duration_minutes }} 分・NT$ {{ Number(svc.price).toLocaleString() }}
                    </span>
                  </div>
                  <button
                    class="bs-btn bs-btn--primary bs-btn--sm"
                    @click="goReserve(svc.id, null)"
                  >
                    立即預約
                  </button>
                </div>
              </template>
            </div>
          </section>

          <!-- 右欄：店家資訊 -->
          <aside class="bs-info">
            <h2 class="bs-section-title">店家資訊</h2>

            <dl class="bs-info__list">
              <template v-if="store.phone">
                <dt>電話</dt>
                <dd>
                  <a :href="`tel:${store.phone}`">{{ store.phone }}</a>
                </dd>
              </template>

              <template v-if="store.line_id">
                <dt>LINE</dt>
                <dd>
                  <a
                    :href="`https://line.me/R/ti/p/${store.line_id.startsWith('@') ? store.line_id : '@' + store.line_id}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="bs-line-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.365 9.863c.349 0 .63.285.63.63 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.105.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></svg>
                    加 LINE 聯絡
                  </a>
                </dd>
              </template>

              <template v-if="store.address">
                <dt>地址</dt>
                <dd>
                  {{ store.address }}
                  <a
                    :href="store.map_url || `https://maps.google.com/maps?q=${encodeURIComponent(store.address)}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="bs-map-link"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/></svg>
                    前往導航
                  </a>
                </dd>
              </template>

              <template v-if="store.description">
                <dt>介紹</dt>
                <dd>{{ store.description }}</dd>
              </template>
            </dl>

            <!-- 營業時間 -->
            <template v-if="bookingStore.currentHours.length > 0">
              <h3 class="bs-hours-title">營業時間</h3>
              <table class="bs-hours-table">
                <tbody>
                  <tr
                    v-for="h in sortedHours"
                    :key="h.day_of_week"
                    :class="{ 'bs-hours-table__row--today': isToday(h.day_of_week) }"
                  >
                    <td class="bs-hours-table__day">{{ dayLabel(h.day_of_week) }}</td>
                    <td>
                      <span v-if="h.is_closed" class="bs-hours-table__closed">公休</span>
                      <span v-else>{{ fmtHour(h.open_time) }} – {{ fmtHour(h.close_time) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
          </aside>
        </div>
      </div>
    </template>

    <!-- 服務聲明 -->
    <div class="bs-disclaimer">
      <p>本平台（JILI）僅提供線上預約媒合與資訊展示服務，各項美容服務均由各合作店家獨立經營並自行提供。本平台不直接參與店家實際服務內容，亦不介入消費者與店家間因服務內容、服務品質、預約異動、退款、退費、損害賠償或其他消費爭議所生之協商與處理，相關權利義務應由消費者與店家雙方自行協調解決。</p>
      <p>為完成預約處理、預約確認、店家聯繫及服務安排，使用者於預約時所提供之姓名、電話及其他必要預約資訊，將提供予該筆預約所屬之店家，供其於預約與服務必要範圍內使用。除依法令規定、主管機關要求，或為處理該筆預約所必要者外，本平台不會將前述資料提供予其他無關第三人或其他未被預約之店家。</p>
      <p>本平台得依營運管理需要，對違反法令、平台規範、侵害消費者權益，或有其他不當行為之店家，採取下架、停權、終止合作或其他必要處置，以維護平台秩序與消費者使用環境。</p>
    </div>

    <!-- 訪客查詢入口（懸浮按鈕） -->
    <BookingQueryFloat />

  </main>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useBookingStore } from '@/store/bookingStore'
import BookingQueryFloat from '@/components/BookingQueryFloat.vue'

const route        = useRoute()
const router       = useRouter()
const bookingStore = useBookingStore()

const storeId      = route.params.id
const store        = computed(() => bookingStore.currentStore)
const isReschedule = route.query.reschedule === '1'

const DAY_LABELS = ['日', '一', '二', '三', '四', '五', '六']
const dayLabel   = (d) => `週${DAY_LABELS[d] ?? d}`

const sortedHours = computed(() =>
  [...bookingStore.currentHours].sort((a, b) => a.day_of_week - b.day_of_week)
)

function resolveImg(url) {
  if (!url) return ''
  if (url.startsWith('/api/booking/img/') && process.env.NODE_ENV === 'development') {
    return `https://jiliskin.com${url}`
  }
  return url.replace(/^http:\/\/127\.0\.0\.1:\d+\/uploads\//, '/uploads/')
}

function isToday(dayOfWeek) {
  return new Date().getDay() === dayOfWeek
}

function fmtHour(timeStr) {
  if (!timeStr) return ''
  const [hStr] = timeStr.split(':')
  const h = parseInt(hStr, 10)
  const m = timeStr.slice(3, 5)
  if (h >= 24) {
    const display = `${String(h - 24).padStart(2, '0')}:${m}`
    return `凌晨 ${display}`
  }
  return `${String(h).padStart(2, '0')}:${m}`
}

// 點「立即預約」，將 serviceId + 服務項目資訊帶到 BookingReserve（query string）
function goReserve(serviceId, item = null) {
  const query = { serviceId }
  if (item) {
    query.itemId       = item.id
    query.itemName     = item.name
    query.itemDuration = item.duration_minutes
    query.itemPrice    = item.price
  }
  router.push({
    name:   'BookingReserve',
    params: { id: storeId },
    query,
  })
}

onMounted(() => {
  bookingStore.fetchStore(storeId)
})

// 離開頁面時清空，避免下一個店家頁面閃爍舊資料
onBeforeUnmount(() => {
  bookingStore.clearCurrentStore()
})
</script>

<style scoped>
/* ── 整體容器 ───────────────────────────────────── */
.booking-store {
  max-width: 1040px;
  margin: 0 auto;
  padding: 24px 0 80px;
}

/* ── 狀態（載入 / 錯誤）────────────────────────── */
.bs-state {
  text-align: center;
  padding: 80px 16px;
  color: #888;
}
.bs-state--error .bs-state__title {
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 8px;
}
.bs-state--error .bs-state__msg {
  color: #c0392b;
  margin-bottom: 24px;
}
.bs-spinner {
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 3px solid #ddd;
  border-top-color: #8b6f5c;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 16px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── 返回首頁列 ─────────────────────────────────── */
.bs-topbar {
  padding: 14px 20px 0;
  margin-bottom: 12px;
}
.bs-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.88rem;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  background: #8b6f5c;
  border-radius: 999px;
  padding: 5px 14px;
  transition: background 0.15s;
}
.bs-back-btn:hover { background: #7a5f4d; }

/* ── 封面 ───────────────────────────────────────── */
.bs-cover {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #ede7e2;
  border-radius: 12px;
}
.bs-cover__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
  filter: brightness(0.88);
}
.bs-cover__placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #ede7e2 0%, #d5cac3 100%);
}
.bs-cover__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 20px 24px;
  background: linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 50%, transparent 100%);
  color: #fff;
}
.bs-cover__name {
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0 0 4px;
  text-shadow: 0 1px 6px rgba(0,0,0,0.5);
}
.bs-cover__address {
  font-size: 0.85rem;
  margin: 0;
  opacity: 0.9;
  text-shadow: 0 1px 3px rgba(0,0,0,0.4);
}

/* ── 主體 ───────────────────────────────────────── */
.bs-cover-wrap {
  padding: 0 20px;
  margin-bottom: 4px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}
.bs-body {
  padding: 16px 20px 0;
}
.bs-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.88rem;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  background: #8b6f5c;
  border-radius: 999px;
  padding: 5px 14px;
  margin-bottom: 20px;
  transition: background 0.15s;
}
.bs-back:hover { background: #7a5f4d; }

.bs-reschedule-banner {
  background: #d4edda;
  color: #155724;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 0.9rem;
  margin-bottom: 16px;
  font-weight: 500;
}

/* ── 雙欄佈局 ───────────────────────────────────── */
.bs-layout {
  display: grid;
  grid-template-columns: 1fr 290px;
  gap: 24px;
  align-items: start;
}

/* ── 區塊標題 ───────────────────────────────────── */
.bs-section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c2c2c;
  border-bottom: 2px solid #e8ddd7;
  padding-bottom: 10px;
  margin: 0 0 16px;
}

/* ── 服務分類標籤 ───────────────────────────────── */
.bs-category-tags {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 20px;
}
.bs-category-tag {
  background: #fdf0e8;
  color: #8b5e3c;
  border: 1px solid #f0c8a0;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
}

/* ── 空服務提示 ─────────────────────────────────── */
.bs-empty {
  color: #bbb;
  font-size: 0.9rem;
  padding: 24px 0;
  text-align: center;
}

/* ── 服務卡片（老師） ───────────────────────────── */
.bs-service-card {
  padding: 18px 20px;
  border: 1px solid #ede7e2;
  border-radius: 12px;
  margin-bottom: 12px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  transition: box-shadow 0.18s;
}
.bs-service-card:hover {
  box-shadow: 0 3px 12px rgba(0,0,0,0.09);
}
.bs-service-card__name {
  font-size: 1rem;
  font-weight: 700;
  color: #2c2c2c;
  margin: 0 0 4px;
}
.bs-service-card__desc {
  font-size: 0.87rem;
  color: #888;
  margin: 0 0 12px;
  line-height: 1.5;
}

/* ── 服務項目列（每老師下的各項服務）──────────── */
.bs-service-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid #f0eae4;
}
.bs-service-item-row:first-of-type {
  margin-top: 4px;
}
.bs-service-item-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
}
.bs-service-item-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
}
.bs-service-item-meta {
  font-size: 0.83rem;
  color: #8b6f5c;
}

/* ── 按鈕 ───────────────────────────────────────── */
.bs-btn {
  padding: 10px 22px;
  border-radius: 8px;
  border: none;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, transform 0.1s;
}
.bs-btn:active { transform: scale(0.97); }
.bs-btn--primary   { background: #8b6f5c; color: #fff; }
.bs-btn--primary:hover  { background: #7a5f4d; }
.bs-btn--secondary {
  background: transparent;
  border: 1px solid #c9a98c;
  color: #8b6f5c;
}
.bs-btn--secondary:hover { background: #f9f0ea; }
.bs-btn--sm {
  padding: 7px 16px;
  font-size: 0.85rem;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── 右欄：店家資訊 ─────────────────────────────── */
.bs-info {
  background: #f9f6f3;
  border: 1px solid #ede5dc;
  border-radius: 12px;
  padding: 20px;
  position: sticky;
  top: 80px;   /* 避開 Navbar */
}
.bs-info .bs-section-title { font-size: 1rem; }

.bs-info__list {
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 10px 10px;
  margin: 0 0 16px;
  font-size: 0.88rem;
}
.bs-info__list dt { color: #aaa; font-weight: 500; }
.bs-info__list dd {
  margin: 0;
  color: #444;
  word-break: break-word;
  line-height: 1.5;
}
.bs-info__list a { color: #8b6f5c; text-decoration: none; }
.bs-info__list a:hover { text-decoration: underline; }

.bs-map-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 7px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #8b6f5c;
  background: #fff;
  border: 1.5px solid #8b6f5c;
  padding: 4px 12px;
  border-radius: 20px;
  text-decoration: none !important;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}
.bs-map-link:hover { background: #8b6f5c; color: #fff; }

.bs-line-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 7px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #fff;
  background: #06c755;
  border: 1.5px solid #06c755;
  padding: 4px 14px;
  border-radius: 20px;
  text-decoration: none !important;
  white-space: nowrap;
  transition: background 0.15s, opacity 0.15s;
}
.bs-line-btn:hover { opacity: 0.85; text-decoration: none !important; }

/* ── 營業時間 ───────────────────────────────────── */
.bs-hours-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #666;
  margin: 0 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.bs-hours-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.86rem;
}
.bs-hours-table td {
  padding: 6px 4px;
  border-bottom: 1px solid #ede7e2;
  color: #555;
}
.bs-hours-table td:last-child { text-align: right; color: #444; }
.bs-hours-table__day   { color: #999; width: 40px; }
.bs-hours-table__closed { color: #ccc; }
.bs-hours-table__row--today td {
  font-weight: 700;
  color: #8b6f5c;
}

/* ── 服務聲明 ───────────────────────────────────── */
.bs-disclaimer {
  margin: 48px 20px 80px;
  font-size: 0.75rem;
  color: #999;
  line-height: 1.7;
}
.bs-disclaimer p {
  text-align: left;
  margin: 0 0 0.75em;
}
.bs-disclaimer p:last-child {
  margin-bottom: 0;
}

/* ── RWD ────────────────────────────────────────── */
@media (max-width: 860px) {
  .bs-layout {
    grid-template-columns: 1fr 240px;
    gap: 16px;
  }
}

@media (max-width: 660px) {
  .bs-cover { aspect-ratio: 1 / 1; }
  .bs-cover__name { font-size: 1.3rem; }
  .bs-cover__overlay { padding: 14px 16px; }

  .bs-body { padding: 14px 14px 0; }

  .bs-layout {
    grid-template-columns: 1fr;
  }

  /* 手機版：店家資訊移到服務項目下方，取消 sticky */
  .bs-info {
    position: static;
    order: 2;
  }
  .bs-services { order: 1; }

  .bs-service-card {
    padding: 16px;
  }
  .bs-service-item-row {
    flex-wrap: wrap;
  }
  .bs-btn--sm {
    width: 100%;
    padding: 10px;
    font-size: 0.9rem;
    margin-top: 4px;
  }
}
</style>
